// The top-level editor component (Point 7 — TipTap migration). Owns a single
// TipTap document instead of a Block[] array: markdown in via markdownToDoc,
// markdown out via docToMarkdown on every edit. Drag-and-drop block reordering
// is dropped (explicitly, per the rework decision record); the slash-command
// palette, floating toolbar, and word-link spans are reimplemented as TipTap
// extensions instead of raw contentEditable DOM manipulation.
import { ExternalLink } from '@/components/words/editor/external-link-mark';
import { SlashCommand } from '@/components/words/editor/slash-command-extension';
import { WordLink, wordLinkClassName } from '@/components/words/editor/word-link-mark';
import { docToMarkdown, markdownToDoc } from '@/lib/markdown-tiptap';
import { useWordsNav } from '@/lib/words-nav';
import type { WordSummary } from '@/schemas/word';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useRef } from 'react';
import { FloatingToolbar } from './floating-toolbar';

interface EditorCanvasProps {
  content: string;
  wordList: WordSummary[];
  /**
   * True once word-detail.tsx's targeted id resolution has settled (succeeded,
   * errored, or had nothing to resolve) — an explicit signal rather than
   * inferring "did wordList really change" from array identity, since the
   * `data = []` query default creates a fresh (but semantically unchanged)
   * empty array on every render while loading, which would otherwise consume
   * the one-time recolor below on stale/empty data before the real result lands.
   */
  linksReady: boolean;
  readOnly?: boolean;
  onChange: (content: string) => void;
}

/**
 * The word body editor. `wordList` (used to colour/resolve word-link spans) is
 * captured once at creation, not kept perfectly live across the session —
 * recreating the whole TipTap editor on every word-list refetch would reset
 * cursor position/undo history mid-edit, a worse trade-off than a few minutes
 * of colour staleness on already-rendered links (matches the existing 60s
 * staleTime on the word-list query elsewhere in the app).
 *
 * The one exception: word-detail.tsx resolves this word's own referenced links
 * by id shortly after mount (a fast batched lookup), and content shouldn't sit
 * behind a spinner until that resolves — so `wordList` is passed to
 * `WordLink.configure()` as a stable array reference (`wordListRef`), whose
 * *contents* (never its identity) get mutated in place once `linksReady` flips
 * true. That alone is enough for click resolution (read live at click time),
 * but NOT for the visual class: ProseMirror reuses a node's existing DOM when
 * its attrs/content haven't changed, so a `setContent` resync does not re-invoke
 * `renderHTML` for a mark whose attrs didn't change. The already-rendered
 * `[data-word-link]` spans' className is patched directly instead (see
 * {@link wordLinkClassName}, shared with the mark so the two never drift).
 * This fires at most once per mount (guarded by `linksReady`, an explicit
 * settled/not-settled signal — see its prop doc for why inferring this from
 * wordList's array identity doesn't work), not on every routine background
 * refetch, so the cursor/undo cost above still holds for the rest of the session.
 */
export function EditorCanvas({
  content,
  wordList,
  linksReady,
  readOnly = false,
  onChange,
}: EditorCanvasProps) {
  const { pushWord } = useWordsNav();
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const wordListRef = useRef(wordList);
  const hasResyncedRef = useRef(false);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
        WordLink.configure({ wordList: wordListRef.current, onNavigate: pushWord }),
        ExternalLink,
        SlashCommand,
      ],
      content: markdownToDoc(content),
      editable: !readOnly,
      onUpdate: ({ editor: e }) => {
        // biome-ignore lint/suspicious/noExplicitAny: TipTap's JSONContent is structurally compatible with our looser DocNode
        onChange(docToMarkdown(e.getJSON() as any));
      },
      editorProps: {
        attributes: { class: 'words-editor__prose' },
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally created once per mount (see docblock); word-detail.tsx already keys the whole page by word.id
    },
    [],
  );

  // Keep editable state in sync when the page's read-only toggle flips, without recreating the editor.
  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  // One-time recolor once the initial link resolution settles (see docblock).
  // Gates on the explicit `linksReady` signal rather than "did wordList change"
  // — never repeats once it's fired, so later background wordList refetches
  // don't keep resetting already-correct spans.
  useEffect(() => {
    if (hasResyncedRef.current || !editor || !linksReady) return;
    hasResyncedRef.current = true;
    // Only copy when `wordList` is a genuinely different array — on mount with
    // `linksReady` already true (nothing to resolve), it's the SAME array
    // `wordListRef` was initialized from; clearing it in place before reading
    // from itself would wipe it out (`length = 0` on a shared reference), never
    // recovering the data `push(...wordList)` was about to copy.
    if (wordList !== wordListRef.current) {
      wordListRef.current.length = 0;
      wordListRef.current.push(...wordList);
    }
    for (const el of editor.view.dom.querySelectorAll<HTMLElement>('[data-word-link]')) {
      el.className = wordLinkClassName(el.dataset.wordId ?? null, wordListRef.current);
    }
  }, [wordList, editor, linksReady]);

  // Resync the document when `content` changes from OUTSIDE this editor (e.g.
  // the quick-add auto-linker applying matches) — never on this editor's own
  // keystrokes, since onUpdate already produces the same string in that case.
  useEffect(() => {
    if (!editor) return;
    // biome-ignore lint/suspicious/noExplicitAny: see onUpdate above
    const current = docToMarkdown(editor.getJSON() as any);
    if (current !== content) {
      editor.commands.setContent(markdownToDoc(content));
    }
  }, [editor, content]);

  return (
    <div ref={editorContainerRef} className="words-editor">
      {!readOnly && editor && (
        <FloatingToolbar editor={editor} editorRef={editorContainerRef} wordList={wordList} />
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
